import {useEffect, useId, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {SlotSlider} from "@pg-design/slot-slider-module";
import {Text} from "@pg-design/text-module";
import {compact, noop} from "@pg-mono/nodash";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {useGetOfferListQuery} from "../../../../offer/api/get_offer_list";
import {OfferBoxPlaceholder} from "../../../../offer/atoms/OfferBoxPlaceholder";
import {OfferBoxWithOfferDetailsAction} from "../../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {IOfferBox} from "../../../../offer/components/offer_box/OfferBox";
import {OfferModal} from "../../../../offer/components/offer_modal/OfferModal";
import {getOrCreateOfferListingAmplitudeItemListId} from "../../../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {OfferType} from "../../../../offer/types/OfferType";
import {getRequestStateFromRtkQuery} from "../../../../store/utils/get_request_state_from_rtk_query";
import {getPricesCityRegionId} from "../../constants/prices_cities_regions";
import {PRICES_HUB_OFFERS_ALL_CITIES, PRICES_HUB_OFFERS_CITY_SLUGS} from "../../constants/prices_hub_cities";
import {usePricesHubOffersViewItemListTracking} from "../../hooks/use_prices_hub_offers_view_item_list_tracking";
import {PRICES_HUB_ANALYTICS_LOCATION, PRICES_HUB_OFFERS_LIST_NAME} from "../../tracking/prices_hub_analytics";
import {trackPricesHubButtonClick} from "../../tracking/track_prices_hub_button_click";
import {trackPricesHubOffersFiltersApplied} from "../../tracking/track_prices_hub_offers_filters_applied";
import {getPricesHubOffersQuery, PRICES_HUB_OFFERS_PAGE_SIZE} from "../../utils/get_prices_offers_query";
import {IPricesHubOffersCityFilterOption, PricesHubOffersCityFilter} from "./PricesHubOffersCityFilter";

const OFFERS_PLACEHOLDERS = [0, 1, 2, 3];
// Matches the pills group padding, so its rounded background edge stays visible next to the active pill.
const CITY_FILTER_SCROLL_OFFSET_PX = 4;
const trackingMeta: IOfferBox["trackingMeta"] = {
    listId: "prices_hub_cheapest_offers",
    listName: PRICES_HUB_OFFERS_LIST_NAME,
    source: "additional",
    tileEvents: {
        imageClick: noop,
        buttonClick: noop,
        linkClick: noop,
        logoClick: noop
    }
};

const cityFilterOptions: IPricesHubOffersCityFilterOption[] = [
    {value: PRICES_HUB_OFFERS_ALL_CITIES, label: "Wszystkie"},
    ...compact(
        PRICES_HUB_OFFERS_CITY_SLUGS.map((slug) => {
            const city = PRICES_CITIES.find((pricesCity) => pricesCity.slug === slug);

            return city ? {value: city.slug, label: city.name} : null;
        })
    )
];

export const PricesHubOffers = () => {
    const headingId = useId();
    const [citySlug, setCitySlug] = useState<string>(PRICES_HUB_OFFERS_ALL_CITIES);
    const regionId = citySlug === PRICES_HUB_OFFERS_ALL_CITIES ? null : getPricesCityRegionId(citySlug);

    const {data, isUninitialized, isSuccess, isError, isLoading, isFetching} = useGetOfferListQuery(getPricesHubOffersQuery(regionId));

    const offers = data?.results ?? [];
    const itemListId = useMemo(() => getOrCreateOfferListingAmplitudeItemListId({type: OfferType.FLAT, ...(regionId ? {region: regionId} : {})}), [regionId]);
    const amplitudeItemListContext = useMemo(() => ({itemListId, itemListName: PRICES_HUB_OFFERS_LIST_NAME}), [itemListId]);
    const offersCarouselRef = usePricesHubOffersViewItemListTracking({offers, itemListId, isFetching});
    const cityFilterScrollRef = useRef<HTMLDivElement>(null);

    // SlotSlider remounts on every city change (key), taking the pills row with it and resetting its scroll to 0.
    // Scrolled horizontally by hand - scrollIntoView would also move the page vertically.
    // Re-run on resize: the slider's arrows show up only after it measures the new offers, narrowing the row.
    useEffect(() => {
        const scrollContainer = cityFilterScrollRef.current;

        if (!scrollContainer) {
            return;
        }

        const revealActivePill = () => {
            const activePill = scrollContainer.querySelector<HTMLElement>("[aria-pressed='true']");

            if (!activePill) {
                return;
            }

            const containerRect = scrollContainer.getBoundingClientRect();
            const pillRect = activePill.getBoundingClientRect();

            // The browser clamps scrollLeft, so the last pills end up as far left as the row allows.
            scrollContainer.scrollLeft += pillRect.left - containerRect.left - CITY_FILTER_SCROLL_OFFSET_PX;
        };

        if (typeof ResizeObserver === "undefined") {
            revealActivePill();
            return;
        }

        const resizeObserver = new ResizeObserver(revealActivePill);

        resizeObserver.observe(scrollContainer);

        return () => {
            resizeObserver.disconnect();
        };
    }, [citySlug]);

    const showPlaceholders = isLoading || (isFetching && offers.length === 0);
    const isEmpty = !showPlaceholders && !isError && offers.length === 0;

    const changeCityFilter = (nextCitySlug: string) => {
        if (nextCitySlug === citySlug) {
            return;
        }

        setCitySlug(nextCitySlug);
        trackPricesHubOffersFiltersApplied({city: cityFilterOptions.find((option) => option.value === nextCitySlug)?.label ?? nextCitySlug});
    };

    return (
        <section className={sectionStyle} aria-labelledby={headingId} aria-busy={isFetching}>
            <Container maxWidthVariant="lg">
                <div className={innerStyle}>
                    <Text id={headingId} as="h2" variant="headline_3" className={headingStyle}>
                        {PRICES_HUB_OFFERS_LIST_NAME}
                    </Text>

                    <div ref={offersCarouselRef}>
                        <SlotSlider
                            key={citySlug}
                            listClassName={listStyle}
                            itemClassName={itemStyle}
                            listLabel="Najtańsze oferty mieszkań"
                            prevLabel="Poprzednie oferty"
                            nextLabel="Następne oferty"
                            onNavigate={(direction) => trackPricesHubButtonClick({buttonName: "rp_apartment_prices_cheapest_offers_arrow", value: direction})}
                            header={
                                <div ref={cityFilterScrollRef} className={filterScrollStyle}>
                                    <PricesHubOffersCityFilter
                                        options={cityFilterOptions}
                                        value={citySlug}
                                        onChange={changeCityFilter}
                                        ariaLabel="Filtruj oferty według miasta"
                                    />
                                </div>
                            }
                        >
                            {isEmpty || isError
                                ? []
                                : showPlaceholders
                                  ? OFFERS_PLACEHOLDERS.map((placeholder) => <OfferBoxPlaceholder key={placeholder} />)
                                  : offers.map((offer, index) => (
                                        <OfferBoxWithOfferDetailsAction
                                            key={offer.id}
                                            offer={offer}
                                            index={index}
                                            trackingMeta={trackingMeta}
                                            amplitudeItemListContext={amplitudeItemListContext}
                                            amplitudeTileLocation={PRICES_HUB_ANALYTICS_LOCATION}
                                        />
                                    ))}
                        </SlotSlider>
                    </div>

                    <p role="status" className={statusStyle}>
                        {isEmpty ? (
                            <Text as="span" variant="body_copy_2">
                                Nie mamy teraz ofert mieszkań w tym mieście.{" "}
                                <button type="button" className={statusActionStyle} onClick={() => changeCityFilter(PRICES_HUB_OFFERS_ALL_CITIES)}>
                                    Zobacz wszystkie oferty
                                </button>
                            </Text>
                        ) : null}
                        {isError ? (
                            <Text as="span" variant="body_copy_2">
                                Nie udało się wczytać ofert. Spróbuj ponownie za chwilę.
                            </Text>
                        ) : null}
                    </p>
                </div>
            </Container>

            <OfferModal
                modalName="mainOfferModal"
                offersQuery={{
                    data: offers,
                    isInitialized: !isUninitialized,
                    isSuccess,
                    isError,
                    isLoading,
                    count: data?.count || 0,
                    page: data?.page || 1,
                    pageSize: data?.page_size || PRICES_HUB_OFFERS_PAGE_SIZE,
                    requestState: getRequestStateFromRtkQuery({isFetching, isSuccess, isError, isLoading})
                }}
            />
        </section>
    );
};

const sectionStyle = css({
    pb: 6,

    md: {
        pb: 9
    }
});

const innerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        gap: 3
    }
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const filterScrollStyle = css({
    display: "flex",
    minWidth: 0,
    py: 0.5,
    overflowX: "auto",
    scrollbarWidth: "none",

    "&::-webkit-scrollbar": {
        display: "none"
    }
});

const statusStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)",

    _empty: {
        position: "absolute",
        width: "1px",
        height: "1px",
        overflow: "hidden",
        clipPath: "inset(50%)",
        whiteSpace: "nowrap"
    }
});

const statusActionStyle = css({
    padding: 0,
    color: "var(--colors-secondary)",
    font: "inherit",
    background: "none",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    }
});

const listStyle = css({
    alignItems: "stretch",
    pb: 3,

    md: {
        gap: 3
    }
});

const itemStyle = css({
    width: "min(34.3rem, 100%)",

    md: {
        width: "36rem"
    }
});
import {useEffect, useId, useMemo, useRef, useState} from "react";
import {css} from "@panda/css";

import {Container} from "@pg-design/grid-module";
import {SlotSlider} from "@pg-design/slot-slider-module";
import {Text} from "@pg-design/text-module";
import {compact, noop} from "@pg-mono/nodash";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {useGetOfferListQuery} from "../../../../offer/api/get_offer_list";
import {OfferBoxPlaceholder} from "../../../../offer/atoms/OfferBoxPlaceholder";
import {OfferBoxWithOfferDetailsAction} from "../../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {IOfferBox} from "../../../../offer/components/offer_box/OfferBox";
import {OfferModal} from "../../../../offer/components/offer_modal/OfferModal";
import {getOrCreateOfferListingAmplitudeItemListId} from "../../../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {OfferType} from "../../../../offer/types/OfferType";
import {getRequestStateFromRtkQuery} from "../../../../store/utils/get_request_state_from_rtk_query";
import {getPricesCityRegionId} from "../../constants/prices_cities_regions";
import {PRICES_HUB_OFFERS_ALL_CITIES, PRICES_HUB_OFFERS_CITY_SLUGS} from "../../constants/prices_hub_cities";
import {usePricesHubOffersViewItemListTracking} from "../../hooks/use_prices_hub_offers_view_item_list_tracking";
import {PRICES_HUB_ANALYTICS_LOCATION, PRICES_HUB_OFFERS_LIST_NAME} from "../../tracking/prices_hub_analytics";
import {trackPricesHubButtonClick} from "../../tracking/track_prices_hub_button_click";
import {trackPricesHubOffersFiltersApplied} from "../../tracking/track_prices_hub_offers_filters_applied";
import {getPricesHubOffersQuery, PRICES_HUB_OFFERS_PAGE_SIZE} from "../../utils/get_prices_offers_query";
import {IPricesHubOffersCityFilterOption, PricesHubOffersCityFilter} from "./PricesHubOffersCityFilter";

const OFFERS_PLACEHOLDERS = [0, 1, 2, 3];
// Matches the pills group padding, so its rounded background edge stays visible next to the active pill.
const CITY_FILTER_SCROLL_OFFSET_PX = 4;
const trackingMeta: IOfferBox["trackingMeta"] = {
    listId: "prices_hub_cheapest_offers",
    listName: PRICES_HUB_OFFERS_LIST_NAME,
    source: "additional",
    tileEvents: {
        imageClick: noop,
        buttonClick: noop,
        linkClick: noop,
        logoClick: noop
    }
};

const cityFilterOptions: IPricesHubOffersCityFilterOption[] = [
    {value: PRICES_HUB_OFFERS_ALL_CITIES, label: "Wszystkie"},
    ...compact(
        PRICES_HUB_OFFERS_CITY_SLUGS.map((slug) => {
            const city = PRICES_CITIES.find((pricesCity) => pricesCity.slug === slug);

            return city ? {value: city.slug, label: city.name} : null;
        })
    )
];

export const PricesHubOffers = () => {
    const headingId = useId();
    const [citySlug, setCitySlug] = useState<string>(PRICES_HUB_OFFERS_ALL_CITIES);
    const regionId = citySlug === PRICES_HUB_OFFERS_ALL_CITIES ? null : getPricesCityRegionId(citySlug);

    const {data, isUninitialized, isSuccess, isError, isLoading, isFetching} = useGetOfferListQuery(getPricesHubOffersQuery(regionId));

    const offers = data?.results ?? [];
    const itemListId = useMemo(() => getOrCreateOfferListingAmplitudeItemListId({type: OfferType.FLAT, ...(regionId ? {region: regionId} : {})}), [regionId]);
    const amplitudeItemListContext = useMemo(() => ({itemListId, itemListName: PRICES_HUB_OFFERS_LIST_NAME}), [itemListId]);
    const offersCarouselRef = usePricesHubOffersViewItemListTracking({offers, itemListId, isFetching});
    const cityFilterScrollRef = useRef<HTMLDivElement>(null);

    // SlotSlider remounts on every city change (key), taking the pills row with it and resetting its scroll to 0.
    // Scrolled horizontally by hand - scrollIntoView would also move the page vertically.
    // Re-run on resize: the slider's arrows show up only after it measures the new offers, narrowing the row.
    useEffect(() => {
        const scrollContainer = cityFilterScrollRef.current;

        if (!scrollContainer) {
            return;
        }

        const revealActivePill = () => {
            const activePill = scrollContainer.querySelector<HTMLElement>("[aria-pressed='true']");

            if (!activePill) {
                return;
            }

            const containerRect = scrollContainer.getBoundingClientRect();
            const pillRect = activePill.getBoundingClientRect();

            // The browser clamps scrollLeft, so the last pills end up as far left as the row allows.
            scrollContainer.scrollLeft += pillRect.left - containerRect.left - CITY_FILTER_SCROLL_OFFSET_PX;
        };

        if (typeof ResizeObserver === "undefined") {
            revealActivePill();
            return;
        }

        const resizeObserver = new ResizeObserver(revealActivePill);

        resizeObserver.observe(scrollContainer);

        return () => {
            resizeObserver.disconnect();
        };
    }, [citySlug]);

    const showPlaceholders = isLoading || (isFetching && offers.length === 0);
    const isEmpty = !showPlaceholders && !isError && offers.length === 0;

    const changeCityFilter = (nextCitySlug: string) => {
        if (nextCitySlug === citySlug) {
            return;
        }

        setCitySlug(nextCitySlug);
        trackPricesHubOffersFiltersApplied({city: cityFilterOptions.find((option) => option.value === nextCitySlug)?.label ?? nextCitySlug});
    };

    return (
        <section className={sectionStyle} aria-labelledby={headingId} aria-busy={isFetching}>
            <Container maxWidthVariant="lg">
                <div className={innerStyle}>
                    <Text id={headingId} as="h2" variant="headline_3" className={headingStyle}>
                        {PRICES_HUB_OFFERS_LIST_NAME}
                    </Text>

                    <div ref={offersCarouselRef}>
                        <SlotSlider
                            key={citySlug}
                            listClassName={listStyle}
                            itemClassName={itemStyle}
                            listLabel="Najtańsze oferty mieszkań"
                            prevLabel="Poprzednie oferty"
                            nextLabel="Następne oferty"
                            onNavigate={(direction) => trackPricesHubButtonClick({buttonName: "rp_apartment_prices_cheapest_offers_arrow", value: direction})}
                            header={
                                <div ref={cityFilterScrollRef} className={filterScrollStyle}>
                                    <PricesHubOffersCityFilter
                                        options={cityFilterOptions}
                                        value={citySlug}
                                        onChange={changeCityFilter}
                                        ariaLabel="Filtruj oferty według miasta"
                                    />
                                </div>
                            }
                        >
                            {isEmpty || isError
                                ? []
                                : showPlaceholders
                                  ? OFFERS_PLACEHOLDERS.map((placeholder) => <OfferBoxPlaceholder key={placeholder} />)
                                  : offers.map((offer, index) => (
                                        <OfferBoxWithOfferDetailsAction
                                            key={offer.id}
                                            offer={offer}
                                            index={index}
                                            trackingMeta={trackingMeta}
                                            amplitudeItemListContext={amplitudeItemListContext}
                                            amplitudeTileLocation={PRICES_HUB_ANALYTICS_LOCATION}
                                        />
                                    ))}
                        </SlotSlider>
                    </div>

                    <p role="status" className={statusStyle}>
                        {isEmpty ? (
                            <Text as="span" variant="body_copy_2">
                                Nie mamy teraz ofert mieszkań w tym mieście.{" "}
                                <button type="button" className={statusActionStyle} onClick={() => changeCityFilter(PRICES_HUB_OFFERS_ALL_CITIES)}>
                                    Zobacz wszystkie oferty
                                </button>
                            </Text>
                        ) : null}
                        {isError ? (
                            <Text as="span" variant="body_copy_2">
                                Nie udało się wczytać ofert. Spróbuj ponownie za chwilę.
                            </Text>
                        ) : null}
                    </p>
                </div>
            </Container>

            <OfferModal
                modalName="mainOfferModal"
                offersQuery={{
                    data: offers,
                    isInitialized: !isUninitialized,
                    isSuccess,
                    isError,
                    isLoading,
                    count: data?.count || 0,
                    page: data?.page || 1,
                    pageSize: data?.page_size || PRICES_HUB_OFFERS_PAGE_SIZE,
                    requestState: getRequestStateFromRtkQuery({isFetching, isSuccess, isError, isLoading})
                }}
            />
        </section>
    );
};

const sectionStyle = css({
    pb: 6,

    md: {
        pb: 9
    }
});

const innerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,

    md: {
        gap: 3
    }
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const filterScrollStyle = css({
    display: "flex",
    minWidth: 0,
    py: 0.5,
    overflowX: "auto",
    scrollbarWidth: "none",

    "&::-webkit-scrollbar": {
        display: "none"
    }
});

const statusStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)",

    _empty: {
        position: "absolute",
        width: "1px",
        height: "1px",
        overflow: "hidden",
        clipPath: "inset(50%)",
        whiteSpace: "nowrap"
    }
});

const statusActionStyle = css({
    padding: 0,
    color: "var(--colors-secondary)",
    font: "inherit",
    background: "none",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    }
});

const listStyle = css({
    alignItems: "stretch",
    pb: 3,

    md: {
        gap: 3
    }
});

const itemStyle = css({
    width: "min(34.3rem, 100%)",

    md: {
        width: "36rem"
    }
});
